import { useCallback, useMemo } from "react";

export const CHAT_FILTER_ALL_KEY = "__chat_filter_all__";

export interface ChatFilterItem {
	id: string;
	name: string;
}

interface UseChatFilterOptions {
	items: ChatFilterItem[];
	selectedIds: string[];
	setSelectedIds: (ids: string[]) => void;
	allLabel: string;
}

export interface UseChatFilterResult {
	allSelected: boolean;
	label: string;
	selectedKeys: string[];
	handleSelect: (args: { key: string }) => void;
	handleDeselect: (args: { key: string }) => void;
}

export function useChatFilter({
	items,
	selectedIds,
	setSelectedIds,
	allLabel
}: UseChatFilterOptions): UseChatFilterResult {
	const allSelected = items.length > 0 && selectedIds.length === items.length;

	const selectAll = useCallback(() => {
		setSelectedIds(items.map(item => item.id));
	}, [items, setSelectedIds]);

	const handleSelect = useCallback(
		({ key }: { key: string }) => {
			if (key === CHAT_FILTER_ALL_KEY) {
				selectAll();
				return;
			}
			if (allSelected) {
				setSelectedIds([key]);
				return;
			}
			if (!selectedIds.includes(key)) {
				setSelectedIds([...selectedIds, key]);
			}
		},
		[allSelected, selectedIds, setSelectedIds, selectAll]
	);

	const handleDeselect = useCallback(
		({ key }: { key: string }) => {
			if (key === CHAT_FILTER_ALL_KEY) {
				selectAll();
				return;
			}
			setSelectedIds(selectedIds.filter(id => id !== key));
		},
		[selectedIds, setSelectedIds, selectAll]
	);

	const label = useMemo(() => {
		if (allSelected || items.length === 0) return allLabel;
		// An empty selection has no name to show; the dropdown falls back to its placeholder.
		if (selectedIds.length === 0) return "";
		const firstName = items.find(item => item.id === selectedIds[0])?.name ?? "";
		if (selectedIds.length === 1) return firstName;
		return `${firstName} + ${selectedIds.length - 1} other${selectedIds.length > 2 ? "s" : ""}`;
	}, [allSelected, items, selectedIds, allLabel]);

	return {
		allSelected,
		label,
		selectedKeys: allSelected ? [] : selectedIds,
		handleSelect,
		handleDeselect
	};
}
